import { useEffect, useRef, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { ApiError } from '@/lib/api/client';
import { beginSetup, confirmSetup, type SessionResult } from './api';
import { CodeEntry } from './CodeEntry';

const errorText = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : 'Something went wrong');

/**
 * Scan the QR code with an authenticator app, then type the code it shows.
 * Two-step only switches on after a correct code, so a mis-scan can't lock
 * anyone out. `mfaToken` is set when this runs during sign-in.
 */
export function SetupPanel({ mfaToken, onEnabled }: {
  mfaToken?: string;
  onEnabled: (result: { recovery_codes: string[] } & Partial<SessionResult>) => void;
}) {
  const [info, setInfo] = useState<{ secret: string; otpauth_uri: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    // Once per mount: every call issues a new secret and invalidates the last QR.
    if (started.current) return;
    started.current = true;
    beginSetup(mfaToken).then(setInfo).catch((e) => setLoadError(errorText(e)));
  }, [mfaToken]);

  if (loadError) return <div className="rounded-(--radius) bg-danger-soft px-3 py-2 text-xs text-danger-ink">{loadError}</div>;
  if (!info) return <div className="py-6 text-center text-sm text-ink-muted">Preparing…</div>;

  const groupedKey = info.secret.match(/.{1,4}/g)?.join(' ') ?? info.secret;

  return (
    <div className="flex flex-col gap-4">
      <ol className="list-decimal space-y-1 pl-5 text-[13px] text-ink">
        <li>Install an authenticator app on your phone (Google Authenticator, Microsoft Authenticator or similar).</li>
        <li>In the app, add an account and scan this code.</li>
        <li>Type the 6-digit code the app shows.</li>
      </ol>
      <div className="flex flex-col items-center gap-2">
        <div className="rounded-lg border border-border bg-white p-3">
          <QRCodeSVG value={info.otpauth_uri} size={168} level="M" />
        </div>
        <div className="text-center text-[11px] text-ink-muted">
          Can’t scan? Enter this key in the app:
          <div className="mt-1 select-all font-mono text-[12px] tracking-wide text-ink">{groupedKey}</div>
        </div>
      </div>
      <CodeEntry
        allowRecovery={false}
        submitLabel="Turn on two-step login"
        pending={pending}
        error={error}
        autoFocus={false}
        onSubmit={async (proof) => {
          if (!('code' in proof)) return;
          setPending(true); setError(null);
          try { onEnabled(await confirmSetup(proof.code, mfaToken)); }
          catch (e) { setError(errorText(e)); }
          finally { setPending(false); }
        }}
      />
    </div>
  );
}
